import React, { useEffect, useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Stack,
  Button,
  Collapse,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  TextField,
  CircularProgress,
} from '@mui/material';
import { Add, PersonAdd, Save } from '@mui/icons-material';
import { toast } from 'react-toastify';
import WorkWeekForm from '../components/WorkWeekForm';
import ViewAllShifts from '../components/ViewAllShifts';
import AssignShiftToEmployee from '../components/AssignShiftToEmployee';
import ShiftForm from '../components/ShiftForm';
import EmployeeWithShift from '../components/EmployeeWithShift';
import LeavePeriodPage from './LeavePeriodPage';
import LeaveTypesPage from './LeaveTypesPage';
import { attendanceApi } from '../../../api/attendanceApi';

const AutoAttendanceProcessTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [enableAutoAttendance, setEnableAutoAttendance] = useState(false);
  const [processTime, setProcessTime] = useState('02:00');
  const [lateMarkAfterMinutes, setLateMarkAfterMinutes] = useState(15);
  const [halfDayAfterHours, setHalfDayAfterHours] = useState(4);
  const [absentAfterHours, setAbsentAfterHours] = useState(8);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const settings = await attendanceApi.getAutoAttendanceSettings();

        setEnableAutoAttendance(settings?.enableAutoAttendance ?? false);
        setProcessTime(settings?.processTime ?? '02:00');
        setLateMarkAfterMinutes(settings?.lateMarkAfterMinutes ?? 15);
        setHalfDayAfterHours(Number(settings?.halfDayAfterHours ?? 4));
        setAbsentAfterHours(Number(settings?.absentAfterHours ?? 8));
      } catch (err: any) {
        toast.error(err.response?.data?.message || 'Failed to load auto attendance settings');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const onSave = async () => {
    setSaving(true);
    try {
      await attendanceApi.saveAutoAttendanceSettings({
        enableAutoAttendance,
        processTime,
        lateMarkAfterMinutes,
        halfDayAfterHours,
        absentAfterHours,
      });

      toast.success('Auto attendance settings saved successfully');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save auto attendance settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Auto Attendance Process
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configure scheduler and attendance thresholds for daily auto processing.
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={enableAutoAttendance}
                  onChange={(e) => setEnableAutoAttendance(e.target.checked)}
                />
              }
              label="Enable Auto Attendance"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Process Time"
              type="time"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={processTime}
              onChange={(e) => setProcessTime(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              label="Late Mark After Minutes"
              type="number"
              fullWidth
              size="small"
              inputProps={{ min: 0 }}
              value={lateMarkAfterMinutes}
              onChange={(e) => setLateMarkAfterMinutes(Number(e.target.value) || 0)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              label="Half Day After Hours"
              type="number"
              fullWidth
              size="small"
              inputProps={{ min: 0, step: 0.5 }}
              value={halfDayAfterHours}
              onChange={(e) => setHalfDayAfterHours(Number(e.target.value) || 0)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              label="Absent After Hours"
              type="number"
              fullWidth
              size="small"
              inputProps={{ min: 0, step: 0.5 }}
              value={absentAfterHours}
              onChange={(e) => setAbsentAfterHours(Number(e.target.value) || 0)}
            />
          </Grid>
        </Grid>

        <Button
          sx={{ mt: 3 }}
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
          onClick={onSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </CardContent>
    </Card>
  );
};

const ShiftSetupTab: React.FC = () => {
  const [subTab, setSubTab] = useState(0);
  const [showAddShift, setShowAddShift] = useState(false);
  const [showAssignShift, setShowAssignShift] = useState(false);

  return (
    <Box>
      <Tabs
        value={subTab}
        onChange={(_, value) => setSubTab(value)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="View All Shifts" />
        <Tab label="Employees With Shift" />
      </Tabs>

      {subTab === 0 && (
        <Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setShowAddShift((prev) => !prev)}
            >
              Add New Shift
            </Button>
            <Button
              variant="outlined"
              startIcon={<PersonAdd />}
              onClick={() => setShowAssignShift((prev) => !prev)}
            >
              Assign Shift to Employee
            </Button>
          </Stack>

          <Collapse in={showAddShift} sx={{ mb: 2 }}>
            <ShiftForm />
          </Collapse>

          <Collapse in={showAssignShift} sx={{ mb: 2 }}>
            <AssignShiftToEmployee />
          </Collapse>

          <ViewAllShifts />
        </Box>
      )}

      {subTab === 1 && <EmployeeWithShift />}
    </Box>
  );
};

const AttendanceLeaveSettingsPage: React.FC = () => {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Attendance & Leave Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Manage work week, shift setup, leave periods, leave types, and auto attendance process in one place.
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Work Week" />
        <Tab label="Shift Setup" />
        <Tab label="Leave Periods" />
        <Tab label="Leave Types" />
        <Tab label="Auto Attendance Process" />
      </Tabs>

      {tab === 0 && <WorkWeekForm />}
      {tab === 1 && <ShiftSetupTab />}
      {tab === 2 && <LeavePeriodPage />}
      {tab === 3 && <LeaveTypesPage />}
      {tab === 4 && <AutoAttendanceProcessTab />}
    </Box>
  );
};

export default AttendanceLeaveSettingsPage;
